/**
 * KAN-743: дерево категорий каталога. Пункт "Все товары" сверху, у каждой
 * категории счётчик товаров по поддереву; ветки сворачиваются. Предки
 * выбранной категории раскрываются сами (revealIds).
 */
import { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, PenLine } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { CategoryNode } from '@/lib/catalogTree';

interface CategoryTreeProps {
  tree: CategoryNode[];
  totalProducts: number;
  selectedId: string | null;
  /** Категории, которые надо раскрыть (предки выбранной). */
  revealIds: string[];
  onSelect: (id: string | null) => void;
}

const rowClass = (active: boolean): string =>
  cn(
    'flex min-w-0 flex-1 items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors',
    active ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'
  );

const countClass = (active: boolean): string =>
  cn('shrink-0 text-xs tabular-nums', active ? 'text-blue-100' : 'text-gray-400');

export function CategoryTree({ tree, totalProducts, selectedId, revealIds, onSelect }: CategoryTreeProps) {
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(revealIds));
  const revealKey = revealIds.join('|');

  useEffect(() => {
    if (!revealKey) return;
    setExpanded((prev) => {
      const next = new Set(prev);
      revealKey.split('|').forEach((id) => next.add(id));
      return next;
    });
  }, [revealKey]);

  const toggle = (id: string): void =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const renderNode = (node: CategoryNode) => {
    const isOpen = expanded.has(node.id);
    const active = node.id === selectedId;
    const hasChildren = node.children.length > 0;
    return (
      <li key={node.id} data-testid={`category-${node.id}`}>
        <div className="flex items-center" style={{ paddingLeft: `${node.depth * 12}px` }}>
          {hasChildren ? (
            <button
              type="button"
              onClick={() => toggle(node.id)}
              aria-expanded={isOpen}
              aria-label={`${isOpen ? 'Свернуть' : 'Развернуть'} ${node.name}`}
              className="flex h-7 w-6 shrink-0 items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            >
              {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
          ) : (
            <span className="w-6 shrink-0" aria-hidden="true" />
          )}
          <button
            type="button"
            onClick={() => onSelect(node.id)}
            aria-current={active ? 'true' : undefined}
            className={rowClass(active)}
          >
            <span className="flex min-w-0 items-center gap-1">
              <span className="truncate" title={node.name}>
                {node.name}
              </span>
              {node.manual && (
                <span role="img" aria-label="Добавлена вручную" title="Добавлена вручную" className="shrink-0">
                  <PenLine
                    aria-hidden="true"
                    className={cn('h-3.5 w-3.5', active ? 'text-blue-100' : 'text-blue-500')}
                  />
                </span>
              )}
            </span>
            <span className={countClass(active)}>{node.subtreeCount}</span>
          </button>
        </div>
        {hasChildren && isOpen && <ul>{node.children.map(renderNode)}</ul>}
      </li>
    );
  };

  const allActive = selectedId === null;
  return (
    <nav aria-label="Категории каталога">
      <ul className="space-y-0.5">
        <li data-testid="category-all" className="flex items-center">
          <span className="w-6 shrink-0" aria-hidden="true" />
          <button
            type="button"
            onClick={() => onSelect(null)}
            aria-current={allActive ? 'true' : undefined}
            className={cn(rowClass(allActive), 'font-medium')}
          >
            <span className="truncate">Все товары</span>
            <span className={countClass(allActive)}>{totalProducts}</span>
          </button>
        </li>
        {tree.map(renderNode)}
      </ul>
    </nav>
  );
}
